/* ═════════════════════════ The Atelier — Curated Catalog ═════════════════════════
   Premium digital luxury catalog.  100% static (no DB):
     • Product data lives in /app/frontend/src/data/atelierData.json
     • Admin (you) edits that file — hot reload refreshes the grid instantly
     • Affiliate links open via the in-app browser (web iframe / Capacitor on mobile)

   Layout rules per spec:
     • 2 items per row (always — even on tablets/desktop the catalog stays
       intentionally premium-sparse)
     • 4:5 portrait card aspect ratio
     • 1px gold/bronze gradient border on every card
     • Dark-marble texture background
     • Minimalist Cinzel + Cormorant typography
     • Discover button with hover/tap glow
     • NO ad units anywhere inside this view
   ─────────────────────────────────────────────────────────────────────────── */

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import axios from 'axios';
import atelierData from '../data/atelierData.json';
import { InAppBrowser } from './InAppBrowser';
import { BACKEND_URL } from '../utils/api';

const GOLD       = '#C5A059';
const GOLD_HI    = '#FFE9A0';
const GOLD_BR_LO = '#8B6B2F';

export const AtelierCatalog = () => {
  const [browserUrl, setBrowserUrl] = useState(null);
  const [browserTitle, setBrowserTitle] = useState('');
  // Start with the bundled JSON so the user never sees an empty grid; the
  // live sheet then overrides it once the backend resolves the CSV.
  const [products, setProducts] = useState(atelierData.products || []);
  const [source, setSource] = useState('bundled');  // bundled | sheet
  const [loading, setLoading] = useState(true);

  // Fetch the live Atelier feed on mount. Re-runs on each app launch (per spec).
  useEffect(() => {
    let cancelled = false;
    axios.get(`${BACKEND_URL}/api/atelier/products`)
      .then(({ data }) => {
        if (cancelled) return;
        if (data?.source === 'sheet' && Array.isArray(data.products) && data.products.length > 0) {
          setProducts(data.products);
          setSource('sheet');
        }
        // else: keep the bundled fallback
      })
      .catch(() => { /* swallow — fallback already in state */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const openLink = (product) => {
    setBrowserUrl(product.link);
    setBrowserTitle(product.name);
  };

  return (
    <div
      className="relative w-full h-full flex flex-col overflow-hidden"
      style={{
        background:
          'repeating-linear-gradient(135deg, rgba(197,160,89,0.018) 0px, rgba(197,160,89,0.018) 2px, transparent 2px, transparent 22px), ' +
          'radial-gradient(ellipse at top, #1a1024 0%, #050308 75%)',
      }}
      data-testid="atelier-catalog">

      {/* Header — title + gold filigree line */}
      <div className="relative flex-shrink-0 px-5 pt-5 pb-4">
        <p className="font-['Outfit'] uppercase tracking-[0.45em] text-center"
          style={{ color: GOLD, fontSize: 9, letterSpacing: '0.45em' }}>
          Curated Catalog
        </p>
        <p className="font-['Cinzel'] text-center mt-1"
          style={{
            color: GOLD_HI,
            fontSize: 26,
            letterSpacing: '0.18em',
            textShadow: '0 0 12px rgba(197,160,89,0.3)',
          }}
          data-testid="atelier-title">
          THE ATELIER
        </p>
        <div className="mt-3 mx-auto max-w-[280px] flex items-center gap-2">
          <div className="flex-1 h-px" style={{
            background: 'linear-gradient(90deg, transparent 0%, rgba(197,160,89,0.6) 50%, transparent 100%)',
          }}/>
          <svg width="10" height="10" viewBox="0 0 10 10">
            <path d="M5 0 L6.5 3.5 L10 5 L6.5 6.5 L5 10 L3.5 6.5 L0 5 L3.5 3.5 Z" fill={GOLD_HI}/>
          </svg>
          <div className="flex-1 h-px" style={{
            background: 'linear-gradient(90deg, transparent 0%, rgba(197,160,89,0.6) 50%, transparent 100%)',
          }}/>
        </div>
        <p className="font-['Cormorant_Garamond'] italic text-center mt-3 mx-auto max-w-md"
          style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13 }}>
          A silent gallery of objects worth keeping.
        </p>
        {/* Live-feed indicator — admin (you) sees at a glance whether the
            catalog is served from the Google Sheet or the bundled fallback. */}
        <p className="text-center mt-2 font-['Outfit'] uppercase tracking-[0.3em]"
          style={{ fontSize: 8, color: source === 'sheet' ? GOLD_HI : 'rgba(197,160,89,0.35)' }}
          data-testid="atelier-source-indicator">
          {loading
            ? '⋯ syncing'
            : source === 'sheet'
              ? `✦ Live · ${products.length} pieces`
              : `Bundled fallback · ${products.length} pieces`}
        </p>
      </div>

      {/* Grid — 2 items per row, 4:5 portrait cards */}
      <div className="flex-1 overflow-y-auto px-4 pb-24" data-testid="atelier-grid">
        <div className="grid grid-cols-2 gap-4">
          {products.map((p, i) => (
            <AtelierCard
              key={p.id}
              product={p}
              index={i}
              onOpen={() => openLink(p)}
            />
          ))}
        </div>
        <p className="text-center font-['Cormorant_Garamond'] italic mt-8 mb-4"
          style={{ color: 'rgba(255,255,255,0.25)', fontSize: 12 }}>
          ✦ End of the gallery · {products.length} curated pieces
        </p>
      </div>

      {/* In-App Browser overlay (affiliate link destination) */}
      <InAppBrowser
        url={browserUrl}
        title={browserTitle}
        onClose={() => setBrowserUrl(null)}
      />
    </div>
  );
};

/* ─────────── Individual product card ─────────── */

const AtelierCard = ({ product, index, onOpen }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.04 * index, duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
      className="relative rounded-2xl overflow-hidden flex flex-col"
      style={{
        // 1px gold/bronze gradient border (per spec)
        background:
          'linear-gradient(180deg, rgba(197,160,89,0.85) 0%, rgba(139,107,47,0.45) 50%, rgba(197,160,89,0.85) 100%)',
        padding: 1,
        boxShadow: '0 0 8px rgba(197,160,89,0.18), 0 2px 6px rgba(0,0,0,0.5)',
      }}
      data-testid={`atelier-card-${product.id}`}>
      <div
        className="flex-1 rounded-[14px] flex flex-col overflow-hidden"
        style={{
          background:
            'radial-gradient(ellipse at 20% 20%, rgba(197,160,89,0.05) 0%, rgba(0,0,0,0) 60%), ' +
            'linear-gradient(180deg, #0c0710 0%, #050308 100%)',
        }}>
        {/* IMAGE — 4:5 portrait (always-displays product even with empty src) */}
        <div
          className="relative w-full"
          style={{ aspectRatio: '4 / 5', borderBottom: `1px solid rgba(197,160,89,0.18)` }}
          data-testid={`atelier-image-${product.id}`}>
          <img
            src={product.image_url}
            alt={product.name}
            className="absolute inset-0 w-full h-full object-contain"
            style={{ padding: '12%' }}
            loading="lazy"
            onError={(e) => { e.currentTarget.style.opacity = 0.15; }}
          />
          {/* Gold diagonal sheen */}
          <div className="absolute inset-0 pointer-events-none" style={{
            background:
              'linear-gradient(140deg, rgba(255,233,160,0.10) 0%, rgba(0,0,0,0) 35%, ' +
              'rgba(0,0,0,0) 70%, rgba(197,160,89,0.06) 100%)',
          }}/>
          {/* Tiny corner brackets */}
          {[
            { top: 4, left: 4,    rotate: 0 },
            { top: 4, right: 4,   rotate: 90 },
            { bottom: 4, right: 4, rotate: 180 },
            { bottom: 4, left: 4,  rotate: 270 },
          ].map((pos, i) => (
            <svg key={i} width="10" height="10" viewBox="0 0 10 10"
              className="absolute pointer-events-none"
              style={{
                top: pos.top, left: pos.left, right: pos.right, bottom: pos.bottom,
                transform: `rotate(${pos.rotate}deg)`,
              }}>
              <path d="M1 0 V1 H0 V0 Z M0 3 V10 H1 V3 Z M3 0 H10 V1 H3 Z"
                fill="rgba(197,160,89,0.55)"/>
            </svg>
          ))}
          {!product.in_stock && (
            <div className="absolute top-2 right-2 px-2 py-0.5 rounded-sm"
              style={{
                background: 'rgba(0,0,0,0.75)', border: `1px solid ${GOLD}33`,
                fontFamily: "'Outfit', sans-serif", fontSize: 7,
                letterSpacing: '0.2em', color: 'rgba(197,160,89,0.7)',
              }}>
              SOLD OUT
            </div>
          )}
        </div>

        {/* TEXT BLOCK */}
        <div className="px-3 pt-3 pb-3 flex-1 flex flex-col">
          <p className="font-['Outfit'] uppercase tracking-[0.25em]"
            style={{ color: GOLD, fontSize: 7.5, letterSpacing: '0.28em' }}>
            {product.category || 'Object'}
          </p>
          <p className="font-['Cinzel'] mt-1"
            style={{
              color: GOLD_HI,
              fontSize: 13,
              lineHeight: 1.2,
              letterSpacing: '0.02em',
              minHeight: '2.4em',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
            data-testid={`atelier-name-${product.id}`}>
            {product.name}
          </p>
          <p className="font-['Cormorant_Garamond'] italic mt-0.5"
            style={{
              color: 'rgba(255,255,255,0.5)',
              fontSize: 11,
              lineHeight: 1.3,
              minHeight: '1.4em',
            }}>
            {product.tagline}
          </p>
          <div className="flex items-end justify-between mt-2.5">
            <p className="font-['Cinzel']" style={{ color: GOLD, fontSize: 14, letterSpacing: '0.04em' }}
              data-testid={`atelier-price-${product.id}`}>
              {product.price}
            </p>
          </div>

          {/* Discover button — gold gradient pill with hover/tap glow */}
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.96 }}
            onClick={onOpen}
            disabled={!product.in_stock}
            className="mt-3 w-full rounded-full px-3 py-2 flex items-center justify-center gap-1.5"
            style={{
              background: product.in_stock
                ? `linear-gradient(140deg, ${GOLD_HI} 0%, ${GOLD} 55%, ${GOLD_BR_LO} 100%)`
                : 'rgba(197,160,89,0.12)',
              color: product.in_stock ? '#0a0610' : 'rgba(197,160,89,0.5)',
              fontFamily: "'Cinzel', serif",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.3em',
              textTransform: 'uppercase',
              boxShadow: product.in_stock ? '0 0 10px rgba(197,160,89,0.35)' : 'none',
              cursor: product.in_stock ? 'pointer' : 'not-allowed',
              transition: 'box-shadow 280ms ease',
            }}
            data-testid={`atelier-discover-${product.id}`}>
            {product.in_stock ? 'Discover' : 'Sold Out'}
            {product.in_stock && <ArrowUpRight size={11} strokeWidth={2.5}/>}
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default AtelierCatalog;
